import { autoinject, BindingEngine, ICollectionObserverSplice } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import { ValidationController, ValidationControllerFactory, ValidationRules } from 'aurelia-validation';
import { State } from '../../services/global-state';
import { ValidationHelpers } from '../../services/validation-helpers';
import { HpfConsumerService, SetCustomerNotificationSettingsRequest } from '../../services/hpf-consumer.service';

@autoinject
export class AddCustomerNotificationPreferences {

	validationController: ValidationController;

	isInvoiceEnabled: boolean = true;
	isEmailOptedIn: boolean;
	isSmsEnabled: boolean = true;
	isSmsOptedIn: boolean;

	// Statement Notifications data source.
	chkStatementNotifications: Array<ICheckBoxGroup> = [];
	// Hidden selected Statement Notifications list.
	hiddenStatementNotifications: Array<number> = [];
	// Visible Selected Statement Notifications list.
	chkGroupStatementNotificiations: Array<number> = [];

	chkAccountNotifications: Array<ICheckBoxGroup> = [];
	hiddenAccountNotifications: Array<number> = [];
	chkGroupAccountNotificiations: Array<number> = [];

	valueEmailAddress: string = '';
	valueEmailAddressConfirm: string = '';
	valueMobileNumber: string = '';

	newCustomer: any;

	constructor(
		private globalState: State,
		private router: Router,
		private i18n: I18N,
		private bindingEngine: BindingEngine,
		private consumerService: HpfConsumerService,
		validationControllerFactory: ValidationControllerFactory) {

		this.isInvoiceEnabled = this.globalState.business.portalSettings.businessSettings.isInvoiceEnabled;
		this.isSmsEnabled = this.globalState.business.portalSettings.businessSettings.enableSMS;

		this.validationController = validationControllerFactory.createForCurrentScope();

		this.bindingEngine.collectionObserver(this.chkGroupStatementNotificiations)
			.subscribe(this.chkGroupStatementNotificationsChanged.bind(this));

		this.bindingEngine.collectionObserver(this.chkGroupAccountNotificiations)
			.subscribe(this.chkGroupAccountNotificationsChanged.bind(this));

		this.chkStatementNotifications = [
			{ value: StatementNotificationType.Paper, label: this.i18n.i18next.t('paper', 'Paper') },
			{ value: StatementNotificationType.Email, label: this.i18n.i18next.t('email', 'Email') },
		];

		this.chkAccountNotifications = [
			{ value: AccountNotificationType.Email, label: this.i18n.i18next.t('email', 'Email') },
		];

		if (this.isSmsEnabled) {
			this.chkAccountNotifications.push(
				{ value: AccountNotificationType.Sms, label: this.i18n.i18next.t('smsText', 'SMS (Text)') },
			);
		}

		this.configureValidationRules();
	}

	attached() {
		this.chkGroupAccountNotificiations.push(AccountNotificationType.Email);

		if (this.isSmsEnabled) {
			this.chkGroupAccountNotificiations.push(AccountNotificationType.Sms);
		}

		this.newCustomer = !!this.globalState.associatedCustomers.length ?
			this.globalState.associatedCustomers[this.globalState.associatedCustomers.length - 1] :
			null;

		if (!this.newCustomer) {
			alert('A newly associated customer is required.');
			this.router.navigateToRoute('dashboard');
		}
	}

	submit() {
		this.validationController.validate()
			.then(response => {
				if (!response.valid) {
					return;
				}

				this.validationController.errors = [];

				let request = new SetCustomerNotificationSettingsRequest();
				request.customerKey = this.newCustomer.customerKey;
				request.emailAddress = this.newCustomer.email;
				request.isEmailOptedIn = this.isEmailOptedIn;
				request.isSmsOptedIn = this.isSmsOptedIn;
				request.isSmsValidated = this.newCustomer.notificationSettings.smsIsValidated;
				request.mobilePhoneNumber = this.valueMobileNumber;

				return this.consumerService.setAssociatedCustomerNotificationPreferences(request);
			})
			.then(response => {
				if (!response.isSuccessful) {
					throw new Error(response.resultMessage);
				}

				let verifySms = this.isSmsOptedIn
					&& !this.newCustomer.notificationSettings.smsIsValidated;

				if (verifySms) {
					let routeSettings = {
						redirectTo: 'dashboard',
						phoneNumber: this.valueMobileNumber,
						customerId: this.newCustomer.id,
					};

					this.router.currentInstruction.config.settings = routeSettings;
					this.router.navigateToRoute('verification');
				} else {
					this.router.navigateToRoute('dashboard');
				}
			})
			.catch(reason => {
				this.handleErrors();
			});
	}

	handleErrors() {
		let baseMessage = this.i18n.i18next.t(
			'unableToRetrieveAccountInformation',
			'We are unable to retrieve your account information, please try again shortly.'
		);

		let message = baseMessage;

		if (this.globalState.business.portalSettings.csPhone) {
			message = baseMessage +
				' ' +
				this.i18n.i18next.t(
					'ifIssuePersistsPleaseContact',
					'If the issue persists please contact us at') +
				' ' +
				this.globalState.business.portalSettings.csPhone +
				'.';
		}

		this.validationController.errors = this.validationController.errors
			.concat([message]);
	}

	cancel() {
		this.router.navigateToRoute('dashboard');
	}

	// Using an observable to update the hidden selected Statement Notifications list.
	// See the ValidationRules comments towards the end of this file for a quick
	// explanation as to why this approach was taken.
	chkGroupStatementNotificationsChanged(splices: Array<ICollectionObserverSplice<number>>) {
		this.hiddenStatementNotifications = this.chkGroupStatementNotificiations;

		this.updateIsEmailOptedIn();
	}

	chkGroupAccountNotificationsChanged(splices: Array<ICollectionObserverSplice<number>>) {
		this.hiddenAccountNotifications = this.chkGroupAccountNotificiations;

		this.updateIsEmailOptedIn();
		this.updateIsSmsOptedIn();
	}

	updateIsEmailOptedIn() {
		this.isEmailOptedIn = this.hiddenStatementNotifications.indexOf(StatementNotificationType.Email) > -1
			|| this.hiddenAccountNotifications.indexOf(AccountNotificationType.Email) > -1;
	}

	updateIsSmsOptedIn() {
		this.isSmsOptedIn = this.isSmsEnabled
			&& this.hiddenAccountNotifications.indexOf(AccountNotificationType.Sms) > -1;
	}

	configureValidationRules() {
		// Required for .ensureObject()
		this.validationController.addObject(this);
		let self = this;
		ValidationRules
			// Validating a hidden control in order to make it easier to display the validation
			// error message once.
			.ensure((x: AddCustomerNotificationPreferences) => x.hiddenStatementNotifications)
			.minItems(1)
			.when((x: AddCustomerNotificationPreferences) => self.isInvoiceEnabled)
			.withMessage(
				this.i18n.i18next.t(
					'aPaperOrEmailStatementNotificationsSelectionIsRequired',
					'A Paper or Email Statement Notifications selection is required.'
				)
			)

			.ensure((x: AddCustomerNotificationPreferences) => x.hiddenAccountNotifications)
			.minItems(1)
			.when((x: AddCustomerNotificationPreferences) => self.isInvoiceEnabled)
			.withMessage(
				this.i18n.i18next.t(
					'anEmailOrSmsAccountNotificationsSelectionIsRequired',
					'An Email or SMS (Text) Account Notifications selection is required.'
				)
			)

			.ensure((x: AddCustomerNotificationPreferences) => x.valueEmailAddress)
			.displayName(
				this.i18n.i18next.t('email', 'Email Address')
			)
			.email()
			.required()
			.when((x: AddCustomerNotificationPreferences) => x.isEmailOptedIn)

			.then()
			.ensure((x: AddCustomerNotificationPreferences) => x.valueEmailAddressConfirm)
			.displayName(
				this.i18n.i18next.t('confirmEmailAddress', 'Confirm Email Address')
			)
			.email()
			.required()
			.when((x: AddCustomerNotificationPreferences) => x.isEmailOptedIn && !!x.valueEmailAddress.length)

			.then()
			.ensureObject()
			.satisfies((x: AddCustomerNotificationPreferences) => x.valueEmailAddressConfirm === x.valueEmailAddress)
			.when((x: AddCustomerNotificationPreferences) => x.isEmailOptedIn
				&& !!x.valueEmailAddressConfirm.length
				&& !!x.valueEmailAddress.length
			)
			.withMessage(
				this.i18n.i18next.t('emailAddressesDoNotMatch', 'The email addresses do not match.')
			)

			.ensure((x: AddCustomerNotificationPreferences) => x.valueMobileNumber)
			.displayName(
				this.i18n.i18next.t('mobileNumber', 'Mobile Number')
			)
			.required()
			.when((x: AddCustomerNotificationPreferences) => x.isSmsOptedIn)

			.ensureObject()
			.satisfies((x: AddCustomerNotificationPreferences) =>
				ValidationHelpers.isPhoneNumber(x.valueMobileNumber)
			)
			.withMessage(
				this.i18n.i18next.t(
					'enterTenDigitPhoneNumber',
					'Please enter a 10 digit mobile phone number.'
				)
			)
			.when((x: AddCustomerNotificationPreferences) => !!x.valueMobileNumber.length)

			.on(this);
	}
}

export interface ICheckBoxGroup {
	value: number;
	label: string;
}

export enum StatementNotificationType {
	Paper = 0,
	Email
}

export enum AccountNotificationType {
	Email = 0,
	Sms
}
